import * as React from "react";
import { styled } from "@mui/material/styles";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Paper,
  useMediaQuery,
} from "@mui/material";
import TextFieldsIcon from "@mui/icons-material/TextFields";   // string methods
import ListIcon from "@mui/icons-material/List";               // array methods
import BuildIcon from "@mui/icons-material/Build";             // general utilities

const StyledCard = styled(Card)(({ theme }) => ({
  width: "100%",
  transition: "transform 0.3s ease, box-shadow 0.3s ease",
  "&:hover": {
    transform: "translateY(-6px) scale(1.02)",
    boxShadow: theme.shadows[6],
    backgroundColor: theme.palette.action.hover,
  },
}));

// Assign icons by category
const rows = [
  {
    name: "charAt",
    description: "Returns the character at the specified index.",
    example: `let text = "Hello, World!";
let character = text.charAt(7);
console.log(character); // Output: "W"`,
    icon: <TextFieldsIcon color="primary" />,
  },
  {
    name: "concat",
    description: "Combines the text of two strings and returns a new string.",
    example: `let str1 = "Hello";
let str2 = "World";
let result = str1.concat(", ", str2);
console.log(result); // Output: "Hello, World"`,
    icon: <TextFieldsIcon color="primary" />,
  },
  {
    name: "charCodeAt",
    description: "Returns the Unicode value of a character at a given index.",
    example: `let text = "ABC";
let code = text.charCodeAt(1);
console.log(code); // Output: 66`,
    icon: <TextFieldsIcon color="primary" />,
  },
  {
    name: "indexOf",
    description: "Finds the first occurrence of a substring and returns its index.",
    example: `let text = "JavaScript is fun!";
let index = text.indexOf("fun");
console.log(index); // Output: 15`,
    icon: <TextFieldsIcon color="primary" />,
  },
  {
    name: "length",
    description: "Returns the length of a string.",
    example: `let text = "Hello!";
console.log(text.length); // Output: 6`,
    icon: <TextFieldsIcon color="primary" />,
  },
  {
    name: "match",
    description: "Matches a regular expression against a string.",
    example: `let text = "The rain in Spain stays mainly in the plain.";
let result = text.match(/ain/g);
console.log(result); // Output: ["ain", "ain", "ain", "ain"]`,
    icon: <TextFieldsIcon color="primary" />,
  },
  {
    name: "replace",
    description: "Replaces a matched substring with a new one.",
    example: `let text = "Hello World";
let newText = text.replace("World", "JavaScript");
console.log(newText); // Output: "Hello JavaScript"`,
    icon: <TextFieldsIcon color="primary" />,
  },
  {
    name: "split",
    description: "Splits a string into an array based on a separator.",
    example: `let text = "apple,banana,cherry";
let fruits = text.split(",");
console.log(fruits); // Output: ["apple", "banana", "cherry"]`,
    icon: <ListIcon color="secondary" />,
  },
  {
    name: "slice",
    description: "Extracts part of a string and returns it.",
    example: `let text = "JavaScript";
let result = text.slice(0, 4);
console.log(result); // Output: "Java"`,
    icon: <TextFieldsIcon color="primary" />,
  },
  {
    name: "substring",
    description: "Extracts characters between two indexes.",
    example: `let text = "JavaScript";
let result = text.substring(4, 10);
console.log(result); // Output: "Script"`,
    icon: <TextFieldsIcon color="primary" />,
  },
  {
    name: "toUpperCase",
    description: "Converts a string to uppercase.",
    example: `let text = "hello world";
console.log(text.toUpperCase()); // Output: "HELLO WORLD"`,
    icon: <TextFieldsIcon color="primary" />,
  },
  {
    name: "toLowerCase",
    description: "Converts a string to lowercase.",
    example: `let text = "HELLO WORLD";
console.log(text.toLowerCase()); // Output: "hello world"`,
    icon: <TextFieldsIcon color="primary" />,
  },
  {
    name: "map",
    description: "Creates a new array with transformed elements.",
    example: `let numbers = [1, 2, 3, 4];
let doubled = numbers.map(num => num * 2);
console.log(doubled); // Output: [2, 4, 6, 8]`,
    icon: <ListIcon color="secondary" />,
  },
  {
    name: "filter",
    description: "Filters elements based on a condition.",
    example: `let numbers = [1, 2, 3, 4, 5];
let evens = numbers.filter(num => num % 2 === 0);
console.log(evens); // Output: [2, 4]`,
    icon: <ListIcon color="secondary" />,
  },
  {
    name: "reduce",
    description: "Applies a function to reduce array elements into a single value.",
    example: `let numbers = [1, 2, 3, 4];
let sum = numbers.reduce((acc, num) => acc + num, 0);
console.log(sum); // Output: 10`,
    icon: <ListIcon color="secondary" />,
  },
];

export default function JavascriptFunctionCards() {
  const isMobile = useMediaQuery("(max-width:600px)");

  return (
      <Paper
          elevation={1}
          sx={{
            backgroundColor: "#fff",
            mb: 4,
            width: "100%",
            p: isMobile ? 2 : 4,
            borderRadius: 3,
            textAlign: "left",
          }}
      >
        <Typography
            variant="h6"
            fontWeight="700"
            textAlign="center"
            sx={{ mb: 4, color: "#100720", fontFamily: "'Roboto', sans-serif" }}
        >
          JavaScript Built-in Functions
        </Typography>

        <Grid container spacing={3} justifyContent="center">
          {rows.map((row) => (
              <Grid item xs={12} sm={6} md={4} key={row.name}>
                <StyledCard>
                  <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      {row.icon}
                      <Typography
                          variant="h6"
                          fontWeight="bold"
                          sx={{ ml: 1, color: "#100720" }}
                      >
                        {row.name}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      {row.description}
                    </Typography>
                    <Typography
                        variant="subtitle2"
                        color="primary"
                        fontWeight="bold"
                        sx={{ mb: 1 }}
                    >
                      Implementation Example:
                    </Typography>
                    <Box
                        sx={{
                          fontFamily: "monospace",
                          backgroundColor: "#f4f4f4",
                          p: 2,
                          borderRadius: 2,
                          fontSize: "0.9rem",
                          whiteSpace: "pre-wrap",
                          overflowX: "auto",
                        }}
                    >
                      {row.example}
                    </Box>
                  </CardContent>
                </StyledCard>
              </Grid>
          ))}
        </Grid>
      </Paper>
  );
}
